import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, Pencil, Plus, RefreshCw, Sparkles, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { TFunction } from 'i18next'
import { friendlyMessage } from '@/api/http'
import { skeletonApi, type ForeshadowItem, type SkeletonLayout, type VolumeSkeleton } from '@/api/skeletonApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { NumberInput } from '@/components/ui/number-input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import type { Project } from '@/types/nf'
import { formatNumber } from '@/utils/format'

export type SkeletonJobOp = 'propose' | 'confirm'
export interface ActiveSkeletonJob {
  jobId: string
  op: SkeletonJobOp
}

interface SkeletonPlannerProps {
  project: Project
  chapterCount: number
  userId?: string
}

function defaultChapterWordTarget(genre?: string | null) {
  const normalized = (genre ?? '').toLowerCase()
  if (normalized.includes('短') || normalized.includes('short')) return 1800
  if (normalized.includes('网文') || normalized.includes('fantasy') || normalized.includes('玄幻')) return 3000
  return 2500
}

function parsePositiveInt(value: string): number | undefined {
  if (!value.trim()) return undefined
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined
  return Math.floor(parsed)
}

function cloneVolumes(volumes: VolumeSkeleton[]): VolumeSkeleton[] {
  return volumes.map((volume) => ({
    ...volume,
    milestones: [...(volume.milestones ?? [])],
    foreshadow_plant: [...(volume.foreshadow_plant ?? [])],
    foreshadow_payoff: [...(volume.foreshadow_payoff ?? [])],
  }))
}

export function formatSkeletonChapterLabel(start: number, end: number, t: TFunction): string {
  if (start === end) return t('skeleton.chapterSingle', { value: start })
  return t('skeleton.chapterRange', { start, end })
}

export function retryFailedSkeletonJob(
  activeJob: ActiveSkeletonJob | null,
  status: string | undefined,
  submit: (op: SkeletonJobOp) => void,
): boolean {
  if (!activeJob || status !== 'failed') return false
  submit(activeJob.op)
  return true
}

function LayoutSummary({ layout }: { layout: SkeletonLayout }) {
  const { t } = useTranslation()
  const totalWords = layout.volumes.reduce((sum, volume) => sum + (volume.word_budget || 0), 0)

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <div className="rounded-md border bg-background/40 p-3">
        <p className="text-xs text-muted-foreground">{t('skeleton.summary.totalChapters')}</p>
        <p className="nf-mono mt-1 text-sm text-foreground">{formatNumber(layout.total_chapters)}</p>
      </div>
      <div className="rounded-md border bg-background/40 p-3">
        <p className="text-xs text-muted-foreground">{t('skeleton.summary.volumeCount')}</p>
        <p className="nf-mono mt-1 text-sm text-foreground">{formatNumber(layout.volume_count)}</p>
      </div>
      <div className="rounded-md border bg-background/40 p-3">
        <p className="text-xs text-muted-foreground">{t('skeleton.summary.totalWords')}</p>
        <p className="nf-mono mt-1 text-sm text-foreground">{formatNumber(totalWords)}</p>
      </div>
    </div>
  )
}

function ReadonlyList({ values, emptyText }: { values: string[]; emptyText: string }) {
  if (!values.length) return <p className="text-sm text-muted-foreground">{emptyText}</p>

  return (
    <ul className="space-y-2">
      {values.map((value, index) => (
        <li key={`${index}-${value}`} className="rounded-md border bg-background/40 p-3 text-sm leading-6">
          {value}
        </li>
      ))}
    </ul>
  )
}

export function ReadonlyForeshadowList({ values, emptyText }: { values: ForeshadowItem[]; emptyText: string }) {
  values = values.filter((item) => item.key?.trim() || item.detail?.trim())
  if (!values.length) return <p className="text-sm text-muted-foreground">{emptyText}</p>

  return (
    <ul className="space-y-2">
      {values.map((item, index) => {
        const key = item.key?.trim()
        const detail = item.detail?.trim()
        const fallback = key || detail || String(index + 1)

        return (
          <li key={`${index}-${fallback}`} className="rounded-md border bg-background/40 p-3 text-sm leading-6">
            {key ? <strong className="font-medium text-foreground">{key}</strong> : null}
            {key && detail ? <span className="text-muted-foreground">: </span> : null}
            {detail ? <span className="text-muted-foreground">{detail}</span> : null}
            {!key && !detail ? <span className="text-muted-foreground">{fallback}</span> : null}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * 提交前剔除【完全空白】的伏笔条目。
 *
 * 「添加埋设/回收」按钮加进来之后，用户可能点出一个空槽却没填。空条目落到后端
 * 会被兜底成 "伏笔N" 占位符，足以让零回收判据误以为有回收 —— 一个能被空白
 * 骗过的门等于没有门。前后端各拦一道：这里让用户不必等到 422 才知道。
 */
export function unsettledSkeletonForeshadows(volumes: VolumeSkeleton[]): string[] {
 const payoffs = new Map<string, number>()
 for (const v of volumes) for (const f of v.foreshadow_payoff ?? []) {const key = f.key?.trim(); if (key) payoffs.set(key, Math.max(payoffs.get(key) ?? 0, v.volume_no))}
 const pending = new Set<string>()
 for (const v of volumes) for (const f of v.foreshadow_plant ?? []) {const key = f.key?.trim(); if (key && !f.is_long_term && (payoffs.get(key) ?? 0) < v.volume_no) pending.add(key)}
 return [...pending].sort()
}

export function stripEmptyForeshadows(volumes: VolumeSkeleton[]): VolumeSkeleton[] {
  const keep = (list: ForeshadowItem[] | undefined) =>
    (list ?? []).filter((item) => (item.key ?? '').trim() !== '' || (item.detail ?? '').trim() !== '')
  return volumes.map((volume) => ({
    ...volume,
    foreshadow_plant: keep(volume.foreshadow_plant),
    foreshadow_payoff: keep(volume.foreshadow_payoff),
  }))
}

export type ForeshadowKind = 'plant' | 'payoff'

const foreshadowField = (kind: ForeshadowKind) =>
  kind === 'plant' ? ('foreshadow_plant' as const) : ('foreshadow_payoff' as const)

/** 把一次伏笔增删改套用到某个卷上。草稿态与编辑态共用同一套逻辑。 */
export function applyForeshadowEdit(
  volume: VolumeSkeleton,
  kind: ForeshadowKind,
  op: { type: 'add' } | { type: 'remove'; index: number } | { type: 'change'; index: number; next: ForeshadowItem },
): VolumeSkeleton {
  const field = foreshadowField(kind)
  const list = [...(volume[field] ?? [])]
  switch (op.type) {
    case 'add':
      list.push({ key: '', detail: '' })
      break
    case 'remove':
      list.splice(op.index, 1)
      break
    case 'change':
      list[op.index] = op.next
      break
  }
  return { ...volume, [field]: list }
}

/**
 * 可编辑的伏笔列表。
 *
 * 「伏笔埋设/兑现」原先两栏都是只读的，而零回收判据拦下骨架后给出的提示是
 * 「请回到骨架规划，为它们安排回收的卷/章后重新确认」——用户照做时会发现
 * 界面里根本没有这个操作，只能反复点「重试」赌模型，或去要求把门关掉。
 * 一道拦得准却没有出口的门，等价于假失效。
 */
function EditableForeshadowList({
  allowLongTerm = false,
  values,
  emptyText,
  onChange,
  onRemove,
}: {
  allowLongTerm?: boolean
  values: ForeshadowItem[]
  emptyText: string
  onChange: (index: number, next: ForeshadowItem) => void
  onRemove: (index: number) => void
}) {
  const { t } = useTranslation()
  if (!values.length) return <p className="text-sm text-muted-foreground">{emptyText}</p>

  return (
    <div className="space-y-2">
      {values.map((item, index) => (
        <div key={index} className="space-y-2 rounded-md border bg-background/40 p-3">
          {allowLongTerm && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(item.is_long_term)} onChange={e=>onChange(index,{...item,is_long_term:e.target.checked})}/>{t('foreshadowDebt.long')}</label>}
          <div className="flex gap-2">
            <Input
              value={item.key ?? ''}
              placeholder={t('skeleton.foreshadowKeyPlaceholder')}
              onChange={(event) => onChange(index, { ...item, key: event.target.value })}
            />
            <Button type="button" variant="ghost" size="icon" onClick={() => onRemove(index)}>
              <Trash2 className="size-4" />
              <span className="sr-only">{t('skeleton.removeForeshadow')}</span>
            </Button>
          </div>
          <Input
            value={item.detail ?? ''}
            placeholder={t('skeleton.foreshadowDetailPlaceholder')}
            onChange={(event) => onChange(index, { ...item, detail: event.target.value })}
          />
        </div>
      ))}
    </div>
  )
}

function VolumeCard({
  volume,
  editable,
  onOutlineChange,
  onMilestoneChange,
  onAddMilestone,
  onRemoveMilestone,
  onForeshadowChange,
  onAddForeshadow,
  onRemoveForeshadow,
}: {
  volume: VolumeSkeleton
  editable: boolean
  onOutlineChange?: (outline: string) => void
  onMilestoneChange?: (index: number, value: string) => void
  onAddMilestone?: () => void
  onRemoveMilestone?: (index: number) => void
  onForeshadowChange?: (kind: ForeshadowKind, index: number, next: ForeshadowItem) => void
  onAddForeshadow?: (kind: ForeshadowKind) => void
  onRemoveForeshadow?: (kind: ForeshadowKind, index: number) => void
}) {
  const { t } = useTranslation()
  const milestones = volume.milestones ?? []
  const plants = volume.foreshadow_plant ?? []
  const payoffs = volume.foreshadow_payoff ?? []

  return (
    <Card className="rounded-lg py-0">
      <CardHeader className="p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              {t('skeleton.volumeTitle', { value: volume.volume_no })}
              <Badge variant="outline">
                {formatSkeletonChapterLabel(volume.start_chapter_no, volume.end_chapter_no, t)}
              </Badge>
            </CardTitle>
            <CardDescription className="mt-2">
              {t('skeleton.wordBudget', { value: formatNumber(volume.word_budget) })}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <Separator />
      <CardContent className="space-y-4 p-4">
        <div className="space-y-2">
          <Label htmlFor={`volume-outline-${volume.volume_no}`}>{t('skeleton.outline')}</Label>
          {editable ? (
            <textarea
              id={`volume-outline-${volume.volume_no}`}
              value={volume.outline}
              rows={7}
              className="min-h-40 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-6 shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 aria-invalid:border-destructive dark:bg-input/30"
              placeholder={t('skeleton.outlinePlaceholder')}
              onChange={(event) => onOutlineChange?.(event.target.value)}
            />
          ) : (
            <p className="rounded-md border bg-background/40 p-3 text-sm leading-6 whitespace-pre-wrap">
              {volume.outline || t('skeleton.emptyOutline')}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label>{t('skeleton.milestones')}</Label>
            {editable ? (
              <Button type="button" variant="ghost" size="sm" onClick={onAddMilestone}>
                <Plus className="size-4" />
                {t('skeleton.addMilestone')}
              </Button>
            ) : null}
          </div>
          {editable ? (
            <div className="space-y-2">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={milestone}
                    placeholder={t('skeleton.milestonePlaceholder')}
                    onChange={(event) => onMilestoneChange?.(index, event.target.value)}
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => onRemoveMilestone?.(index)}>
                    <Trash2 className="size-4" />
                    <span className="sr-only">{t('skeleton.removeMilestone')}</span>
                  </Button>
                </div>
              ))}
              {!milestones.length ? <p className="text-sm text-muted-foreground">{t('skeleton.emptyMilestones')}</p> : null}
            </div>
          ) : (
            <ReadonlyList values={milestones} emptyText={t('skeleton.emptyMilestones')} />
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {(['plant', 'payoff'] as const).map((kind) => {
            const values = kind === 'plant' ? plants : payoffs
            return (
              <div key={kind} className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label>{t(kind === 'plant' ? 'skeleton.foreshadowPlant' : 'skeleton.foreshadowPayoff')}</Label>
                  {editable ? (
                    <Button type="button" variant="ghost" size="sm" onClick={() => onAddForeshadow?.(kind)}>
                      <Plus className="size-4" />
                      {t(kind === 'plant' ? 'skeleton.addForeshadowPlant' : 'skeleton.addForeshadowPayoff')}
                    </Button>
                  ) : null}
                </div>
                {editable ? (
                  <EditableForeshadowList allowLongTerm={kind === 'plant'}
                    values={values}
                    emptyText={t('skeleton.emptyForeshadow')}
                    onChange={(index, next) => onForeshadowChange?.(kind, index, next)}
                    onRemove={(index) => onRemoveForeshadow?.(kind, index)}
                  />
                ) : (
                  <ReadonlyForeshadowList values={values} emptyText={t('skeleton.emptyForeshadow')} />
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function PlannerLoading() {
  return (
    <Card className="rounded-lg">
      <CardHeader>
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-64 max-w-full" />
      </CardHeader>
      <CardContent className="space-y-3">
        <Skeleton className="h-20 rounded-md" />
        <Skeleton className="h-40 rounded-md" />
      </CardContent>
    </Card>
  )
}

const VOLUMES_PER_PAGE = 5

function usePagination<T>(items: T[], pageSize: number) {
  const [page, setPage] = useState(0)
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize))
  const safePage = Math.min(page, pageCount - 1)
  const pageItems = items.slice(safePage * pageSize, safePage * pageSize + pageSize)
  return { page: safePage, pageCount, setPage, pageItems }
}

function VolumePager({
  page,
  pageCount,
  onPage,
}: {
  page: number
  pageCount: number
  onPage: (page: number) => void
}) {
  const { t } = useTranslation()
  if (pageCount <= 1) return null
  return (
    <div className="flex items-center justify-center gap-3 pt-3">
      <Button type="button" variant="outline" size="sm" disabled={page <= 0} onClick={() => onPage(page - 1)}>
        {t('skeleton.prevPage')}
      </Button>
      <span className="nf-mono text-sm text-muted-foreground">
        {t('skeleton.pageIndicator', { page: page + 1, total: pageCount })}
      </span>
      <Button type="button" variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => onPage(page + 1)}>
        {t('skeleton.nextPage')}
      </Button>
    </div>
  )
}

export function SkeletonPlanner({ project, chapterCount, userId }: SkeletonPlannerProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [targetWords, setTargetWords] = useState(String(project.target_words ?? ''))
  const [chapterWordTarget, setChapterWordTarget] = useState(
    project.chapter_word_target ? String(project.chapter_word_target) : '',
  )
  const [layout, setLayout] = useState<SkeletonLayout | null>(null)
  const [draftVolumes, setDraftVolumes] = useState<VolumeSkeleton[]>([])
  const [isReplanning, setIsReplanning] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editVolumes, setEditVolumes] = useState<VolumeSkeleton[]>([])
  const [activeJob, setActiveJob] = useState<ActiveSkeletonJob | null>(null)

  const skeletonQuery = useQuery({
    queryKey: ['skeleton', userId, project.id],
    queryFn: () => skeletonApi.get(project.id),
    enabled: Boolean(project.id),
  })

  const confirmedVolumes = skeletonQuery.data ?? []
  const hasConfirmedSkeleton = confirmedVolumes.length > 0
  const confirmedPager = usePagination(confirmedVolumes, VOLUMES_PER_PAGE)
  const editPager = usePagination(editVolumes, VOLUMES_PER_PAGE)
  const draftPager = usePagination(draftVolumes, VOLUMES_PER_PAGE)
  const chapterWordPlaceholder = defaultChapterWordTarget(project.genre)
  const parsedChapterWordTarget = parsePositiveInt(chapterWordTarget)
  const parsedTargetWords = parsePositiveInt(targetWords)
  const confirmChapterWordTarget = useMemo(() => {
    if (parsedChapterWordTarget) return parsedChapterWordTarget
    if (!layout?.total_chapters) return chapterWordPlaceholder
    const totalWords = layout.volumes.reduce((sum, volume) => sum + (volume.word_budget || 0), 0)
    return Math.max(1, Math.round(totalWords / layout.total_chapters))
  }, [chapterWordPlaceholder, layout, parsedChapterWordTarget])

  const proposeMutation = useMutation({
    mutationFn: () =>
      skeletonApi.propose(project.id, {
        target_words: parsedTargetWords,
        chapter_word_target: parsedChapterWordTarget,
      }),
    onSuccess: (data) => {
      setActiveJob({ jobId: data.job_id, op: 'propose' })
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('skeleton.proposeFailed')))
    },
  })

  const confirmMutation = useMutation({
    mutationFn: () =>
      skeletonApi.confirm(project.id, {
        chapter_word_target: confirmChapterWordTarget,
        volumes: stripEmptyForeshadows(draftVolumes),
      }),
    onSuccess: async (data) => {
      setActiveJob({ jobId: data.job_id, op: 'confirm' })
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('skeleton.confirmFailed')))
    },
  })

  const skeletonJobQuery = useQuery({
    queryKey: ['skeleton-job', userId, project.id, activeJob?.jobId],
    queryFn: () => skeletonApi.job(project.id, activeJob?.jobId ?? ''),
    enabled: Boolean(project.id && activeJob?.jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'done' || status === 'failed' ? false : 2000
    },
  })

  useEffect(() => {
    const job = activeJob
    const data = skeletonJobQuery.data
    if (!job || !data) return
    if (data.status === 'failed') {
      toast.error(data.error || t(job.op === 'propose' ? 'skeleton.proposeFailed' : 'skeleton.confirmFailed'))
      return
    }
    if (data.status !== 'done') return
    if (job.op === 'propose') {
      if (data.result?.layout) setLayout(data.result.layout)
      setDraftVolumes(cloneVolumes(data.result?.volumes ?? []))
      toast.success(t('skeleton.proposeDone'))
      setActiveJob(null)
      return
    }
    toast.success(t('skeleton.confirmDone'))
    setLayout(null)
    setDraftVolumes([])
    setIsReplanning(false)
    setActiveJob(null)
    void queryClient.invalidateQueries({ queryKey: ['skeleton', userId, project.id] })
    void queryClient.invalidateQueries({ queryKey: ['project', userId, project.id] })
    void queryClient.invalidateQueries({ queryKey: ['chapters', userId, project.id] })
  }, [activeJob, project.id, queryClient, skeletonJobQuery.data, t, userId])

  const updateMutation = useMutation({
    mutationFn: () =>
      skeletonApi.update(project.id, {
        chapter_word_target: confirmChapterWordTarget,
        volumes: stripEmptyForeshadows(editVolumes),
      }),
    onSuccess: async () => {
      toast.success(t('skeleton.updateDone'))
      setIsEditing(false)
      setEditVolumes([])
      await queryClient.invalidateQueries({ queryKey: ['skeleton', userId, project.id] })
      await queryClient.invalidateQueries({ queryKey: ['project', userId, project.id] })
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('skeleton.updateFailed')))
    },
  })

  const updateVolume = (volumeNo: number, updater: (volume: VolumeSkeleton) => VolumeSkeleton) => {
    setDraftVolumes((volumes) => volumes.map((volume) => (volume.volume_no === volumeNo ? updater(volume) : volume)))
  }

  const updateEditVolume = (volumeNo: number, updater: (volume: VolumeSkeleton) => VolumeSkeleton) => {
    setEditVolumes((volumes) => volumes.map((volume) => (volume.volume_no === volumeNo ? updater(volume) : volume)))
  }

  const startEditing = () => {
    setIsEditing(true)
    setEditVolumes(cloneVolumes(confirmedVolumes))
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setEditVolumes([])
  }

  const showPlanningForm = !hasConfirmedSkeleton || isReplanning || draftVolumes.length > 0
  const activeJobStatus = skeletonJobQuery.data?.status
  const isActiveJobFailed = Boolean(activeJob) && activeJobStatus === 'failed'
  const isSkeletonJobRunning = Boolean(activeJob) && activeJobStatus !== 'done' && activeJobStatus !== 'failed'
  const pendingForeshadows = unsettledSkeletonForeshadows(draftVolumes)
  const canConfirm = pendingForeshadows.length === 0 && draftVolumes.length > 0 && !confirmMutation.isPending && !proposeMutation.isPending && !isSkeletonJobRunning
  const retrySkeletonJob = () => {
    const job = activeJob
    retryFailedSkeletonJob(job, activeJobStatus, (op) => {
      setActiveJob(null)
      if (job?.jobId) {
        queryClient.removeQueries({ queryKey: ['skeleton-job', userId, project.id, job.jobId] })
      }
      if (op === 'propose') proposeMutation.mutate()
      else confirmMutation.mutate()
    })
  }

  if (skeletonQuery.isLoading) return <PlannerLoading />

  return (
    <div className="space-y-4">
      {chapterCount === 0 ? (
        <div className="rounded-lg border border-dashed bg-background/45 p-4 text-sm text-muted-foreground">
          {t('skeleton.noChaptersHint')}
        </div>
      ) : null}

      {skeletonQuery.error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {friendlyMessage(skeletonQuery.error, t('skeleton.loadFailed'))}
        </div>
      ) : null}

      {hasConfirmedSkeleton && !showPlanningForm && !isEditing ? (
        <Card className="rounded-lg py-0">
          <CardHeader className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Check className="size-4 text-primary" />
                  {t('skeleton.confirmedTitle')}
                </CardTitle>
                <CardDescription className="mt-2">{t('skeleton.confirmedHint')}</CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={startEditing}>
                  <Pencil className="size-4" />
                  {t('skeleton.edit')}
                </Button>
                <Button variant="outline" onClick={() => setIsReplanning(true)}>
                  <RefreshCw className="size-4" />
                  {t('skeleton.replan')}
                </Button>
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="p-4">
            <ScrollArea className="h-[calc(100vh-20rem)] min-h-96">
              <div className="space-y-4 pr-3">
                {confirmedPager.pageItems.map((volume) => (
                  <VolumeCard key={volume.volume_no} volume={volume} editable={false} />
                ))}
              </div>
            </ScrollArea>
            <VolumePager
              page={confirmedPager.page}
              pageCount={confirmedPager.pageCount}
              onPage={confirmedPager.setPage}
            />
          </CardContent>
        </Card>
      ) : null}

      {hasConfirmedSkeleton && isEditing ? (
        <Card className="rounded-lg py-0">
          <CardHeader className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Pencil className="size-4 text-primary" />
                  {t('skeleton.editTitle')}
                </CardTitle>
                <CardDescription className="mt-2">{t('skeleton.editHint')}</CardDescription>
              </div>
              <Button type="button" variant="ghost" onClick={cancelEditing} disabled={updateMutation.isPending}>
                {t('common.cancel')}
              </Button>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="p-4">
            <ScrollArea className="h-[calc(100vh-22rem)] min-h-96">
              <div className="space-y-4 pr-3">
                {editPager.pageItems.map((volume) => (
                  <VolumeCard
                    key={volume.volume_no}
                    volume={volume}
                    editable
                    onOutlineChange={(outline) => updateEditVolume(volume.volume_no, (item) => ({ ...item, outline }))}
                    onMilestoneChange={(index, value) =>
                      updateEditVolume(volume.volume_no, (item) => {
                        const milestones = [...(item.milestones ?? [])]
                        milestones[index] = value
                        return { ...item, milestones }
                      })
                    }
                    onAddMilestone={() =>
                      updateEditVolume(volume.volume_no, (item) => ({
                        ...item,
                        milestones: [...(item.milestones ?? []), ''],
                      }))
                    }
                    onRemoveMilestone={(index) =>
                      updateEditVolume(volume.volume_no, (item) => ({
                        ...item,
                        milestones: (item.milestones ?? []).filter((_, itemIndex) => itemIndex !== index),
                      }))
                    }
                    onForeshadowChange={(kind, index, next) =>
                      updateEditVolume(volume.volume_no, (item) =>
                        applyForeshadowEdit(item, kind, { type: 'change', index, next }),
                      )
                    }
                    onAddForeshadow={(kind) =>
                      updateEditVolume(volume.volume_no, (item) => applyForeshadowEdit(item, kind, { type: 'add' }))
                    }
                    onRemoveForeshadow={(kind, index) =>
                      updateEditVolume(volume.volume_no, (item) =>
                        applyForeshadowEdit(item, kind, { type: 'remove', index }),
                      )
                    }
                  />
                ))}
              </div>
            </ScrollArea>
            <VolumePager page={editPager.page} pageCount={editPager.pageCount} onPage={editPager.setPage} />
            <div className="flex flex-wrap items-center justify-end gap-3 border-t pt-4">
              <Button variant="ghost" onClick={cancelEditing} disabled={updateMutation.isPending}>
                {t('common.cancel')}
              </Button>
              <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending || !editVolumes.length}>
                {updateMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                {updateMutation.isPending ? t('skeleton.saving') : t('skeleton.save')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {showPlanningForm ? (
        <Card className="rounded-lg py-0">
          <CardHeader className="p-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="size-4 text-primary" />
              {hasConfirmedSkeleton ? t('skeleton.replanTitle') : t('skeleton.title')}
            </CardTitle>
            <CardDescription>{t('skeleton.subtitle')}</CardDescription>
          </CardHeader>
          <Separator />
          <CardContent className="space-y-4 p-4">
            <form
              className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
              onSubmit={(event) => {
                event.preventDefault()
                proposeMutation.mutate()
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="skeleton-target-words">{t('skeleton.targetWords')}</Label>
                <NumberInput
                  id="skeleton-target-words"
                  min={1}
                  value={parsePositiveInt(targetWords)}
                  placeholder={t('skeleton.targetWordsPlaceholder')}
                  onChange={(value) => setTargetWords(value === undefined ? '' : String(value))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="skeleton-chapter-word-target">{t('skeleton.chapterWordTarget')}</Label>
                <NumberInput
                  id="skeleton-chapter-word-target"
                  min={1}
                  value={parsePositiveInt(chapterWordTarget)}
                  placeholder={t('skeleton.chapterWordTargetPlaceholder', { value: chapterWordPlaceholder })}
                  onChange={(value) => setChapterWordTarget(value === undefined ? '' : String(value))}
                />
              </div>
              <div className="flex items-end gap-2">
                {hasConfirmedSkeleton ? (
                  <Button type="button" variant="ghost" onClick={() => setIsReplanning(false)}>
                    {t('common.cancel')}
                  </Button>
                ) : null}
                <Button type="submit" disabled={proposeMutation.isPending || isSkeletonJobRunning}>
                  {proposeMutation.isPending || (activeJob?.op === 'propose' && isSkeletonJobRunning) ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Sparkles className="size-4" />
                  )}
                  {proposeMutation.isPending || (activeJob?.op === 'propose' && isSkeletonJobRunning) ? t('skeleton.proposing') : t('skeleton.propose')}
                </Button>
              </div>
            </form>

            {isActiveJobFailed ? (
              <div className="flex flex-col gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between">
                <span>{skeletonJobQuery.data?.error || t(activeJob?.op === 'confirm' ? 'skeleton.confirmFailed' : 'skeleton.proposeFailed')}</span>
                <Button type="button" variant="outline" size="sm" onClick={retrySkeletonJob}>
                  <RefreshCw className="size-4" />
                  {t('common.retry')}
                </Button>
              </div>
            ) : null}

            {proposeMutation.isPending || (activeJob?.op === 'propose' && isSkeletonJobRunning) ? (
              <div className="rounded-lg border bg-background/40 p-4 text-sm text-muted-foreground">
                <div className="mb-3 flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin text-primary" />
                  {t('skeleton.proposingHint')}
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-16 rounded-md" />
                  <Skeleton className="h-24 rounded-md" />
                </div>
              </div>
            ) : null}

            {layout ? <LayoutSummary layout={layout} /> : null}

            {draftVolumes.length ? (
              <>
                <ScrollArea className="h-[calc(100vh-22rem)] min-h-96">
                  <div className="space-y-4 pr-3">
                    {draftPager.pageItems.map((volume) => (
                      <VolumeCard
                        key={volume.volume_no}
                        volume={volume}
                        editable
                        onOutlineChange={(outline) => updateVolume(volume.volume_no, (item) => ({ ...item, outline }))}
                        onMilestoneChange={(index, value) =>
                          updateVolume(volume.volume_no, (item) => {
                            const milestones = [...(item.milestones ?? [])]
                            milestones[index] = value
                            return { ...item, milestones }
                          })
                        }
                        onAddMilestone={() =>
                          updateVolume(volume.volume_no, (item) => ({
                            ...item,
                            milestones: [...(item.milestones ?? []), ''],
                          }))
                        }
                        onRemoveMilestone={(index) =>
                          updateVolume(volume.volume_no, (item) => ({
                            ...item,
                            milestones: (item.milestones ?? []).filter((_, itemIndex) => itemIndex !== index),
                          }))
                        }
                        onForeshadowChange={(kind, index, next) =>
                          updateVolume(volume.volume_no, (item) =>
                            applyForeshadowEdit(item, kind, { type: 'change', index, next }),
                          )
                        }
                        onAddForeshadow={(kind) =>
                          updateVolume(volume.volume_no, (item) => applyForeshadowEdit(item, kind, { type: 'add' }))
                        }
                        onRemoveForeshadow={(kind, index) =>
                          updateVolume(volume.volume_no, (item) =>
                            applyForeshadowEdit(item, kind, { type: 'remove', index }),
                          )
                        }
                      />
                    ))}
                  </div>
                </ScrollArea>
                {pendingForeshadows.length > 0 && <p role="alert" className="text-sm text-destructive">{t('foreshadowDebt.decisions')}: {pendingForeshadows.join('、')}</p>}
                <VolumePager page={draftPager.page} pageCount={draftPager.pageCount} onPage={draftPager.setPage} />
                <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
                  <p className="text-sm text-muted-foreground">
                    {t('skeleton.confirmHint', { value: formatNumber(confirmChapterWordTarget) })}
                  </p>
                  <Button onClick={() => confirmMutation.mutate()} disabled={!canConfirm}>
                    {confirmMutation.isPending || (activeJob?.op === 'confirm' && isSkeletonJobRunning) ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Pencil className="size-4" />
                    )}
                    {confirmMutation.isPending || (activeJob?.op === 'confirm' && isSkeletonJobRunning)
                      ? t('skeleton.confirming')
                      : t('skeleton.confirm')}
                  </Button>
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
